import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageCircle, Send, Loader2, CheckCircle, HelpCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ContactSupportDialog = ({ 
  trigger, 
  defaultName = '', 
  defaultEmail = '',
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  
  const [form, setForm] = useState({
    name: defaultName,
    email: defaultEmail,
    subject: '',
    message: '',
    category: 'general'
  });

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'course', label: 'Course Related' },
    { value: 'account', label: 'Account Help' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit request');
      }

      setTicketId(data.ticket_id);
      setSubmitted(true);
      toast.success('Support request submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to submit support request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset after close animation
    setTimeout(() => {
      setSubmitted(false);
      setTicketId('');
      setForm({
        name: defaultName,
        email: defaultEmail,
        subject: '',
        message: '',
        category: 'general'
      });
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => isOpen ? setOpen(true) : handleClose()}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className={className} data-testid="contact-support-btn">
            <HelpCircle className="h-4 w-4 mr-2" />
            Contact Support
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            <MessageCircle className="inline h-5 w-5 mr-2" />
            Contact Support
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center" data-testid="support-success">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>
              Request Submitted!
            </h3>
            <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
              We've received your message and sent a confirmation to your email.
            </p>
            <div 
              className="inline-block px-4 py-2 rounded-lg text-sm font-mono"
              style={{ backgroundColor: '#E3E1DB', color: '#163329' }}
            >
              Ticket ID: {ticketId}
            </div>
            <p className="text-xs mt-4" style={{ color: '#5C635A' }}>
              We typically respond within 24-48 hours.
            </p>
            <Button onClick={handleClose} className="mt-6">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="support-name">Name *</Label>
                <Input
                  id="support-name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                  required
                  data-testid="support-name-input"
                />
              </div>
              <div>
                <Label htmlFor="support-email">Email *</Label>
                <Input
                  id="support-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                  data-testid="support-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="support-category">Category</Label>
              <Select 
                value={form.category} 
                onValueChange={(v) => setForm(f => ({ ...f, category: v }))}
              >
                <SelectTrigger data-testid="support-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="support-subject">Subject *</Label>
              <Input
                id="support-subject"
                value={form.subject}
                onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Brief description of your inquiry"
                required
                data-testid="support-subject-input"
              />
            </div>

            <div>
              <Label htmlFor="support-message">Message *</Label>
              <Textarea
                id="support-message"
                value={form.message}
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Please describe your question or issue in detail..."
                rows={4}
                required
                data-testid="support-message-input"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                data-testid="support-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupportDialog;
